import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import { 
  FiPercent, 
  FiSearch, 
  FiInfo, 
  FiRefreshCw, 
  FiTruck, 
  FiTrendingUp,
  FiPlus
} from "react-icons/fi";
import axios from "axios";
import { baseurl } from "../../baseurl";

function ChargesByCategory() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  
  const STANDARD_DELIVERY = 40; // Fixed delivery charge

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseurl}getCategories`);
      setCategories(res.data.categories || []);
    } catch (err) {
      console.error("Failed to fetch charges", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const filteredCategories = categories.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper bg-light" style={{ minHeight: "100vh" }}>
        <div className="main-content p-4">
          
          {/* Header Section */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
            <div>
              <h4 className="fw-bold mb-0 text-dark">Selling & Delivery Fees</h4>
              <p className="text-muted small mb-0">Transparent breakdown of platform fees and logistics costs</p>
            </div>
            <div className="d-flex gap-2">
              <div className="position-relative">
                <FiSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                <input
                  type="text"
                  className="form-control ps-5 shadow-sm border-0"
                  style={{ width: "260px", borderRadius: "10px" }}
                  placeholder="Filter categories..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <button className="btn btn-white shadow-sm border-0 rounded-3" onClick={fetchCategories}>
                <FiRefreshCw className={loading ? "spinner-border spinner-border-sm border-0" : ""} />
              </button>
            </div>
          </div>

          {/* Logistics & Fee Info */}
          <div className="row g-3 mb-4">
            <div className="col-md-8">
              <div className="alert border-0 shadow-sm rounded-4 d-flex align-items-center gap-3 mb-0 h-100" style={{ backgroundColor: '#eef2ff', color: '#4338ca' }}>
                <div className="bg-white p-2 rounded-circle shadow-sm"><FiInfo size={20} /></div>
                <div>
                  <span className="fw-bold d-block">Fee Policy</span>
                  <small className="opacity-75">Marketplace fees apply to the item value. Delivery charges are fixed per order regardless of category.</small>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm rounded-4 h-100 bg-primary text-white">
                <div className="card-body d-flex align-items-center gap-3">
                  <div className="bg-secondary p-2 rounded-circle text-white"><FiTruck size={20} /></div>
                  <div>
                    <span className="small d-block opacity-75">Std. Delivery</span>
                    <h5 className="fw-bold mb-0 text-white">₹{STANDARD_DELIVERY}.00 <small className="small fw-normal">/ order</small></h5>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Category Grid */}
          <div className="row g-4">
            {loading ? (
              <div className="col-12 text-center py-5">
                <div className="spinner-border text-primary" role="status"></div>
              </div>
            ) : filteredCategories.map((cat) => (
              <div className="col-md-6 col-lg-4 col-xl-3" key={cat.id}>
                <div className="card h-100 border-0 shadow-sm charge-card" style={{ borderRadius: "16px" }}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div className="category-init-box">{cat.name.charAt(0)}</div>
                      <span className="badge bg-soft-success text-success rounded-pill px-2 py-1 x-small">Active</span>
                    </div>
                    
                    <h6 className="fw-bold text-dark mb-3">{cat.name}</h6>

                    {/* Admin Charge Row */}
                    <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                      <span className="small text-muted d-flex align-items-center gap-1"><FiPercent size={12}/> Admin Fee</span>
                      <span className="fw-bold text-dark">{parseFloat(cat.admin_charges).toFixed(2)}%</span>
                    </div>

                    {/* Delivery Charge Row */}
                    <div className="d-flex justify-content-between align-items-center mb-3 px-1">
                      <span className="small text-muted d-flex align-items-center gap-1"><FiTruck size={12}/> Delivery</span>
                      <span className="fw-bold text-dark">₹{STANDARD_DELIVERY}</span>
                    </div>

                    <div className="p-3 rounded-4 bg-light text-center border">
                      <p className="x-small text-muted mb-1 text-uppercase fw-bold">Estimated Deduction</p>
                      <h5 className="fw-bold mb-0 text-primary">
                        {parseFloat(cat.admin_charges).toFixed(1)}% + ₹{STANDARD_DELIVERY}
                      </h5>
                    </div>

                    <div className="mt-3 pt-3 border-top d-flex justify-content-between align-items-center">
                        <span className="x-small text-muted">Payout ID</span>
                        <span className="x-small fw-bold text-dark">#CAT-00{cat.id}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {!loading && filteredCategories.length === 0 && (
            <div className="text-center py-5">
              <p className="text-muted">No categories found.</p>
            </div>
          )}
        </div>
      </main>

      <style>{`
        .x-small { font-size: 0.7rem; }
        .bg-soft-success { background-color: #dcfce7; }
        
        .charge-card { transition: all 0.2s ease; }
        .charge-card:hover { transform: translateY(-5px); box-shadow: 0 10px 20px rgba(0,0,0,0.05) !important; }

        .category-init-box {
            width: 38px;
            height: 38px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            color: #232f3e;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 800;
        }

        .btn-white { background: #fff; border: 1px solid #eee; }
      `}</style>
    </>
  );
}

export default ChargesByCategory;